// src/components/dnl/AddPointDialog.tsx
/**
 * Add Point Dialog Component
 * Phase 1: Custom/Sparse Points System
 * 
 * Modal dialog for adding custom points to the double number line.
 * Supports:
 * - Value entry
 * - Optional custom label
 * - Line selection (primary/secondary)
 * - "?" unknown value styling
 * - Auto-add corresponding point on other line
 * 
 * Based on DNL Enhancement Specification v1.0, Section 4.2
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Plus, HelpCircle } from 'lucide-react';
import { RootState } from '../../types/redux';
import {
  addCustomTick,
  addCustomTickWithCorresponding,
} from '../../store/slices/dnlToolsSlice';

// =============================================================================
// TYPES
// =============================================================================

export interface AddPointDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Pre-filled target line */
  initialLine?: 'primary' | 'secondary';
  /** Pre-filled value (e.g., from click position) */
  initialValue?: number;
  /** Dark mode */
  isDark?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const AddPointDialog: React.FC<AddPointDialogProps> = ({
  isOpen,
  onClose,
  initialLine = 'secondary',
  initialValue,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  const valueInputRef = useRef<HTMLInputElement>(null);

  // Get scale factor from redux
  const secondaryLine = useSelector((state: RootState) => state.secondaryLine);
  const scaleFactor = secondaryLine.scaleFactor;

  // Form state
  const [targetLine, setTargetLine] = useState<'primary' | 'secondary'>(initialLine);
  const [value, setValue] = useState<string>(initialValue?.toString() ?? '');
  const [label, setLabel] = useState<string>('');
  const [showAsQuestion, setShowAsQuestion] = useState<boolean>(false);
  const [addCorresponding, setAddCorresponding] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTargetLine(initialLine);
      setValue(initialValue?.toString() ?? '');
      setLabel('');
      setShowAsQuestion(false);
      setAddCorresponding(false);
      setError('');
      
      // Focus the value input after a short delay
      setTimeout(() => {
        valueInputRef.current?.focus();
        valueInputRef.current?.select();
      }, 100);
    }
  }, [isOpen, initialLine, initialValue]);

  // Calculate corresponding value preview
  const correspondingValue = useCallback(() => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;
    
    const factor = scaleFactor.n / scaleFactor.d;
    return targetLine === 'primary'
      ? numValue * factor
      : numValue / factor;
  }, [value, targetLine, scaleFactor]);

  // Handle form submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    // Convert secondary line value to domain (primary) value
    // Custom ticks store values in the domain coordinate system
    // Secondary line values need to be converted: domainValue = secondaryValue / scaleFactor
    const factor = scaleFactor.n / scaleFactor.d;
    const domainValue = targetLine === 'secondary'
      ? numValue / factor
      : numValue;

    const tickLabel = showAsQuestion ? '?' : (label || undefined);
    const style = showAsQuestion ? 'question' : 'normal';

    if (addCorresponding) {
      dispatch(addCustomTickWithCorresponding({
        value: domainValue,
        line: targetLine,
        label: tickLabel,
        style,
        scaleFactor,
      }));
    } else {
      dispatch(addCustomTick({
        value: domainValue,
        line: targetLine,
        label: tickLabel,
        style,
      }));
    }

    onClose();
  }, [value, label, showAsQuestion, addCorresponding, targetLine, scaleFactor, dispatch, onClose]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  // Styles
  const overlayClass = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
  const dialogClass = `rounded-lg shadow-xl max-w-md w-full mx-4 ${
    isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
  }`;
  const headerClass = `flex items-center justify-between p-4 border-b ${
    isDark ? 'border-gray-700' : 'border-gray-200'
  }`;
  const inputClass = `w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${
    isDark ? 'text-gray-300' : 'text-gray-700'
  }`;
  const radioLabelClass = `flex items-center gap-2 cursor-pointer ${
    isDark ? 'text-gray-300' : 'text-gray-700'
  }`;
  const checkboxLabelClass = `flex items-center gap-2 cursor-pointer text-sm ${
    isDark ? 'text-gray-300' : 'text-gray-600'
  }`;
  const buttonPrimaryClass = 'px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2';
  const buttonSecondaryClass = `px-4 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors ${
    isDark
      ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
  }`;

  const correspValue = correspondingValue();

  return (
    <div 
      className={overlayClass} 
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-point-title"
    >
      <div 
        className={dialogClass} 
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={headerClass}>
          <h2 id="add-point-title" className="text-lg font-semibold flex items-center gap-2">
            <Plus size={20} />
            Add Custom Point
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-md transition-colors ${
              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Line Selection */}
          <div>
            <label className={labelClass}>Line</label>
            <div className="flex gap-4 mt-1">
              <label className={radioLabelClass}>
                <input
                  type="radio"
                  name="line"
                  value="primary"
                  checked={targetLine === 'primary'}
                  onChange={() => setTargetLine('primary')}
                  className="w-4 h-4 text-blue-600"
                />
                Primary
              </label>
              <label className={radioLabelClass}>
                <input
                  type="radio"
                  name="line"
                  value="secondary"
                  checked={targetLine === 'secondary'}
                  onChange={() => setTargetLine('secondary')}
                  className="w-4 h-4 text-blue-600"
                />
                Secondary
              </label>
            </div>
          </div>

          {/* Value Input */}
          <div>
            <label htmlFor="point-value" className={labelClass}>
              Value
            </label>
            <input
              ref={valueInputRef}
              id="point-value"
              type="number"
              step="any"
              value={value}
              onChange={e => {
                setValue(e.target.value);
                setError('');
              }}
              className={inputClass}
              placeholder="Enter a number (e.g., 125)"
              required
            />
            {error && (
              <p className="mt-1 text-sm text-red-500">{error}</p>
            )}
          </div>

          {/* Label Input */}
          <div>
            <label htmlFor="point-label" className={labelClass}>
              Label <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>(optional)</span>
            </label>
            <input
              id="point-label"
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              className={inputClass}
              placeholder="Custom label (e.g., 125g)"
              disabled={showAsQuestion}
            />
          </div>

          {/* Show as Question Checkbox */}
          <div>
            <label className={checkboxLabelClass}>
              <input
                type="checkbox"
                checked={showAsQuestion}
                onChange={e => setShowAsQuestion(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <HelpCircle size={16} className="text-amber-500" />
              Show as "?" (unknown value)
            </label>
          </div>

          {/* Add Corresponding Point Checkbox */}
          <div className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <label className={checkboxLabelClass}>
              <input
                type="checkbox"
                checked={addCorresponding}
                onChange={e => setAddCorresponding(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              Add corresponding point on other line (using scale factor)
            </label>
            {addCorresponding && correspValue !== null && (
              <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Corresponding value on {targetLine === 'primary' ? 'secondary' : 'primary'} line:{' '}
                <strong className={isDark ? 'text-white' : 'text-gray-900'}>
                  {correspValue.toFixed(4).replace(/\.?0+$/, '')}
                </strong>
                <br />
                <span className="text-xs">
                  (Scale factor: {scaleFactor.n}/{scaleFactor.d} = {(scaleFactor.n / scaleFactor.d).toFixed(4)})
                </span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className={`flex justify-end gap-3 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={onClose}
              className={buttonSecondaryClass}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={buttonPrimaryClass}
            >
              <Plus size={16} />
              Add Point
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPointDialog;
